/**
 * Typed WebSocket wrapper.
 *
 * Responsibilities:
 *   • Connect → send `hello` → dispatch parsed server messages.
 *   • Assign monotonic `seq` per input; reset on reconnect (WP1 semantics).
 *   • Reject raw `send()` — callers pass typed builder args only.
 *   • Idempotent connect for React 18 StrictMode double-mount.
 *   • Expose `subscribe` for message routing (welcome, snapshot, phase,
 *     protocolMismatch, etc.) — no ambient globals.
 *
 * Design notes (see wp7-client-ui-input.md § "WebSocket client"):
 *   • parse EVERY inbound frame via `parseServerMessage` — malformed
 *     bytes log + ignore, never crash the UI.
 *   • Disconnect handling stops here — no auto-reconnect at MVP; the
 *     UI shows a banner and routes back to Home.
 *
 * Testability: the constructor takes a factory for the WebSocket-like
 * object. Unit tests inject a fake with the four handlers we care
 * about (`onopen`, `onmessage`, `onclose`, `onerror`).
 */

import {
  MAX_CHAT_TEXT_LEN,
  PROTOCOL_VERSION,
  parseServerMessage,
  serializeClientMessage,
  toSeq,
  type ClientMessage,
  type Loadout,
  type OpponentSeat,
  type ServerMessage,
  type SimInput,
  type MatchId,
  type ChampionId,
  type QueueSpec,
  type Seq,
} from "@pit/protocol";
import { loadSessionToken, loadTesterAccessCode, saveSessionToken } from "../prefs.js";

/** Minimal WebSocket-like shape. Real `WebSocket` satisfies this. */
export interface WsLike {
  readonly send: (data: string) => void;
  readonly close: (code?: number, reason?: string) => void;
  readonly readyState: number;
  onopen: ((ev: unknown) => void) | null;
  onclose: ((ev: unknown) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
}

export type WsFactory = (url: string) => WsLike;

/** Life-cycle status emitted to subscribers. */
export type ConnectionStatus =
  | { readonly kind: "idle" }
  | { readonly kind: "connecting" }
  | {
      readonly kind: "welcomed";
      readonly sessionId: string;
      /** #497: resolved server-side tester flag for THIS connection —
       *  mirrors `welcome.testerFlagged` verbatim. */
      readonly testerFlagged: boolean;
    }
  | { readonly kind: "protocolMismatch"; readonly serverVersion: string }
  | { readonly kind: "closed"; readonly clean: boolean };

/** Subscriber callbacks. Every parsed server message flows through
 *  `onMessage`. Status changes flow separately for UI wiring. */
export interface SocketListeners {
  readonly onStatus?: (s: ConnectionStatus) => void;
  readonly onMessage?: (m: ServerMessage) => void;
  /** Called when a parse fails — kept for QA logging. Never triggers UI. */
  readonly onParseError?: (raw: unknown) => void;
}

/** Options for `PitSocket`. */
export interface PitSocketOptions {
  readonly url: string;
  readonly factory: WsFactory;
  readonly listeners?: SocketListeners;
}

/**
 * Typed pit socket. Connect-once (idempotent), send strictly typed
 * `ClientMessage`s.
 */
export class PitSocket {
  private readonly url: string;
  private readonly factory: WsFactory;
  private listeners: SocketListeners;
  private ws: WsLike | null = null;
  private nextSeq = 0;
  private status: ConnectionStatus = { kind: "idle" };
  private disposed = false;

  constructor(options: PitSocketOptions) {
    this.url = options.url;
    this.factory = options.factory;
    this.listeners = options.listeners ?? {};
  }

  /** Attach / replace listeners after construction (React lifecycle). */
  setListeners(listeners: SocketListeners): void {
    this.listeners = listeners;
  }

  /** Current status. */
  getStatus(): ConnectionStatus {
    return this.status;
  }

  /**
   * Open the socket. Idempotent — if already opened (or opening) this
   * is a no-op, so React StrictMode's double-mount is safe.
   *
   * Also REVIVES a disposed socket: StrictMode's mount → cleanup →
   * mount cycle calls `dispose()` then `connect()` on the same memoized
   * instance, so `connect()` must clear the disposed flag and open a
   * fresh underlying WebSocket. Stray post-dispose callbacks can't
   * re-open anything because `dispose()` detaches all handlers.
   */
  connect(): void {
    this.disposed = false;
    if (this.ws !== null) return;
    this.setStatus({ kind: "connecting" });
    this.nextSeq = 0;
    const ws = this.factory(this.url);
    this.ws = ws;
    ws.onopen = (): void => {
      this.sendHello();
    };
    ws.onmessage = (ev): void => {
      this.handleMessage(ev.data);
    };
    ws.onclose = (): void => {
      const wasWelcomed = this.status.kind === "welcomed";
      this.ws = null;
      // Clean = at least got a welcome; unclean = dropped mid-handshake.
      this.setStatus({ kind: "closed", clean: wasWelcomed });
    };
    ws.onerror = (): void => {
      // Browsers wrap the underlying reason; the close event will fire
      // right after so we surface state via `onclose`. Log path could
      // go through `onParseError` in the future.
    };
  }

  /**
   * Send a `hello` message with our protocol version, the tester access
   * code if configured (#497), and the persisted `@pit/data` session
   * token if we have one (#533) — the server resolves it back to the SAME
   * account this browser used last time; absent/expired/invalid ⇒ the
   * server transparently mints a fresh guest account instead (see
   * `welcome.sessionToken`'s doc comment / `handleMessage` below, which
   * persists whatever the server hands back).
   */
  private sendHello(): void {
    const testerAccessCode = loadTesterAccessCode();
    const sessionToken = loadSessionToken();
    this.sendClient({
      type: "hello",
      protocolVersion: PROTOCOL_VERSION,
      testerAccessCode: testerAccessCode.length > 0 ? testerAccessCode : undefined,
      sessionToken: sessionToken.length > 0 ? sessionToken : undefined,
    });
  }

  /**
   * Send a `SimInput` — wraps it with the next seq and serializes.
   * Returns the seq used, or `null` if the send couldn't happen (not
   * connected). Callers can use the returned seq for local echo (WP4+).
   */
  sendInput(input: SimInput): Seq | null {
    if (this.ws === null) return null;
    let seq: Seq;
    try {
      seq = toSeq(this.nextSeq);
    } catch {
      return null;
    }
    this.nextSeq += 1;
    this.sendClient({ type: "input", seq, input });
    return seq;
  }

  /**
   * Send `createMatch` with the chosen champion + loadout (#323).
   * `opts.teammateSeats`/`opts.opponentSeats` (#676, was #499's singular
   * `opponentSeat`) and `opts.mapSlug` (#678) are practice-mode-only —
   * omitted for every ordinary create (today's behavior, byte-identical);
   * present only from a gated/ungated practice-mode screen. The server
   * independently re-checks `isTesterFlagged()` (with #609's dummy
   * carve-out) for any request carrying seats, regardless of what this
   * client sends.
   */
  createMatch(
    championId: ChampionId,
    loadout: Loadout,
    opts?: {
      readonly teammateSeats?: readonly OpponentSeat[];
      readonly opponentSeats?: readonly OpponentSeat[];
      readonly mapSlug?: string;
    },
  ): void {
    this.sendClient({
      type: "createMatch",
      championId,
      loadout,
      ...(opts?.teammateSeats !== undefined ? { teammateSeats: [...opts.teammateSeats] } : {}),
      ...(opts?.opponentSeats !== undefined ? { opponentSeats: [...opts.opponentSeats] } : {}),
      ...(opts?.mapSlug !== undefined ? { mapSlug: opts.mapSlug } : {}),
    });
  }

  /** Send `joinMatch` with the chosen champion + loadout (#323). */
  joinMatch(matchId: MatchId, championId: ChampionId, loadout: Loadout): void {
    this.sendClient({ type: "joinMatch", matchId, championId, loadout });
  }

  /** Send `quickJoin` for a matchmaking queue with the chosen champion +
   *  loadout (#323). */
  quickJoin(queue: QueueSpec, championId: ChampionId, loadout: Loadout): void {
    this.sendClient({ type: "quickJoin", queue, championId, loadout });
  }

  /**
   * Send `leaveMatch` — explicitly detach from whatever room this
   * connection is currently in (#332). Distinct from just navigating away
   * client-side: without this, the server keeps `state.room` bound to the
   * abandoned room forever, and every subsequent create/join/quickJoin on
   * this connection is rejected with `badMessage`.
   */
  leaveMatch(): void {
    this.sendClient({ type: "leaveMatch" });
  }

  /**
   * Request the promoted, 1v1-eligible bot roster (#499) — the practice-
   * mode setup screen's bot-opponent picker data source. The server
   * answers with `botList` (or `error: unauthorized` for a non-tester-
   * flagged connection).
   */
  listBots(): void {
    this.sendClient({ type: "listBots" });
  }

  /**
   * Send an in-game chat message (#833). The server stamps the speaking seat
   * from this connection and attaches the text to the next snapshot's `chat`
   * array; there is no ack.
   *
   * Trims and length-caps LOCALLY before sending: an over-cap string would be
   * rejected as a MALFORMED frame by the server pipeline (costing a
   * malformed strike, three of which close the socket), so the client must
   * never emit one. A blank message is dropped outright rather than sent —
   * the server would drop it anyway, and there is nothing to say.
   */
  sendChat(text: string): void {
    const trimmed = text.trim().slice(0, MAX_CHAT_TEXT_LEN);
    if (trimmed.length === 0) return;
    this.sendClient({ type: "chat", text: trimmed });
  }

  /**
   * Typed send — only entry point for outbound bytes. Raw `send()`
   * bypass would defeat WP1's discipline; keep this the single site.
   */
  private sendClient(msg: ClientMessage): void {
    if (this.ws === null) return;
    let payload: string;
    try {
      payload = serializeClientMessage(msg);
    } catch {
      // Serializer drift: dev-only re-parse threw. Better to swallow
      // than crash the tab; a bug report will surface it upstream.
      return;
    }
    try {
      this.ws.send(payload);
    } catch {
      // Send-after-close (browsers throw sometimes). No auto-reconnect
      // at MVP — the close handler will already have fired or is about
      // to. Silent drop.
    }
  }

  /** Parse and dispatch a single wire frame. */
  private handleMessage(data: unknown): void {
    let raw: unknown = data;
    if (typeof data === "string") {
      try {
        raw = JSON.parse(data);
      } catch {
        this.listeners.onParseError?.(data);
        return;
      }
    }
    const parsed = parseServerMessage(raw);
    if (!parsed.ok) {
      this.listeners.onParseError?.(raw);
      return;
    }
    const msg = parsed.value;
    // Life-cycle bookkeeping for welcome / protocolMismatch — kept in
    // the socket layer so consumers can treat status changes and
    // ordinary messages symmetrically.
    if (msg.type === "welcome") {
      // #533: a non-null `sessionToken` means the server just minted a NEW
      // session (first-ever connect, or our previous token had expired/was
      // invalid) — persist it so the NEXT `hello` resolves back to this
      // SAME account instead of fragmenting into another throwaway guest.
      // `null` means the token we already sent was valid and reused
      // as-is — nothing new to persist.
      if (msg.sessionToken !== null) {
        saveSessionToken(msg.sessionToken);
      }
      this.setStatus({
        kind: "welcomed",
        sessionId: msg.sessionId,
        testerFlagged: msg.testerFlagged,
      });
    } else if (msg.type === "protocolMismatch") {
      this.setStatus({
        kind: "protocolMismatch",
        serverVersion: msg.serverVersion,
      });
    }
    this.listeners.onMessage?.(msg);
  }

  private setStatus(s: ConnectionStatus): void {
    this.status = s;
    this.listeners.onStatus?.(s);
  }

  /**
   * Close and mark disposed. Sends/callbacks become no-ops until a
   * subsequent `connect()` revives the instance (StrictMode remount).
   */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    const ws = this.ws;
    this.ws = null;
    if (ws !== null) {
      // Drop handlers first so close doesn't re-enter status updates.
      ws.onopen = null;
      ws.onclose = null;
      ws.onerror = null;
      ws.onmessage = null;
      try {
        ws.close();
      } catch {
        // Ignored — close on already-closed is fine.
      }
    }
  }
}

/**
 * Default WebSocket factory — real browser WebSocket. Tests inject a
 * fake instead.
 */
export const browserWsFactory: WsFactory = (url) =>
  new WebSocket(url) as unknown as WsLike;
